import { useMemo, useState } from 'react'
import * as Mtds from '@mattilsynet/design/react'
import { CalendarDotsIcon } from '@phosphor-icons/react'
import { ConfirmDeletionModal } from '../confirm-deletion-modal/ConfirmDeletionModal'
import { DuedateTimeline } from '../duedate-timeline/DuedateTimeline'
import { getSortedDuedates, toLocaleDateString } from '../../utils/dateUtils'
import { useErrorToast } from '../../index'
import { AddDuedateModal } from './AddDuedateModal'

type DuedatesSectionProps = {
  duedates: string[] | null | undefined
  hideHeading?: boolean
  isPending?: boolean
  onAddDuedate?: (duedate: string) => void | Promise<void>
  onRemoveDuedate?: (duedate: string) => void | Promise<void>
}

export const DuedatesSection = ({
  duedates,
  hideHeading = false,
  isPending = false,
  onAddDuedate,
  onRemoveDuedate
}: DuedatesSectionProps) => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [deletePendingDuedate, setDeletePendingDuedate] = useState<
    string | null
  >(null)

  const sortedDuedates = useMemo(() => getSortedDuedates(duedates), [duedates])
  const isEditable = onAddDuedate != null || onRemoveDuedate != null
  const showRemoveError = useErrorToast('fjerne fristen')

  if (sortedDuedates.length === 0 && !isEditable) {
    return null
  }

  const handleRemoveDuedate = (): void => {
    if (deletePendingDuedate == null) return

    void Promise.resolve(onRemoveDuedate?.(deletePendingDuedate))
      .catch((error) => {
        showRemoveError(error)
      })
      .finally(() => {
        setDeletePendingDuedate(null)
      })
  }

  const section = (
    <Mtds.Grid data-gap='8'>
      {!hideHeading && (
        <Mtds.Flex data-justify='space-between' data-align='center'>
          <Mtds.Heading data-size={'xs'}>
            <CalendarDotsIcon />
            Frister
          </Mtds.Heading>
        </Mtds.Flex>
      )}

      {sortedDuedates.length > 0 || onAddDuedate != null ? (
        <DuedateTimeline
          duedates={sortedDuedates}
          isPending={isPending}
          onAddDuedate={
            onAddDuedate == null ? undefined : () => setIsAddModalOpen(true)
          }
          onRemoveDuedate={
            onRemoveDuedate == null ? undefined : setDeletePendingDuedate
          }
        />
      ) : (
        <Mtds.Prose>Ingen innsendingsfrister er satt</Mtds.Prose>
      )}

      {onAddDuedate != null && (
        <AddDuedateModal
          isOpen={isAddModalOpen}
          setOpen={setIsAddModalOpen}
          onAddDuedate={onAddDuedate}
        />
      )}

      {isEditable && (
        <ConfirmDeletionModal
          isOpen={deletePendingDuedate !== null}
          setOpen={(open) => {
            if (!open) setDeletePendingDuedate(null)
          }}
          isPending={isPending}
          title={`Fristen ${toLocaleDateString(deletePendingDuedate ?? undefined) ?? ''} vil bli fjernet.`}
          onConfirm={handleRemoveDuedate}
          heading='Bekreft fjerning'
          confirmLabel='Fjern'
        />
      )}
    </Mtds.Grid>
  )

  return section
}
